import { activeKey, isReadyForCloud, type Settings } from "../data/types";
import type { ModelInfo } from "../data/modelStore";
import { createCloudEngine } from "./cloudEngine";
import { createLocalEngine, isWebGpuAvailable } from "./localEngine";
import type { LlmEngine } from "./types";

/**
 * Resolves the LLM engine the rest of the app should drive features with.
 *
 * Precedence:
 *   1. Cloud, when the user explicitly enabled it and supplied an API key.
 *   2. Local Gemma, when WebGPU is available and a model file is installed.
 *   3. null — caller renders a "configure an engine" prompt.
 *
 * This matches the spec's "local-first by default, cloud as opt-in upgrade"
 * stance: as long as the cloud toggle is off, we route through Gemma.
 */
export function getActiveEngine(
  settings: Settings,
  installedModel: ModelInfo | null,
): LlmEngine | null {
  if (isReadyForCloud(settings)) {
    return createCloudEngine(settings.selectedProvider, activeKey(settings));
  }
  if (installedModel && isWebGpuAvailable()) {
    return createLocalEngine(installedModel.fileName);
  }
  return null;
}

export function activeEngineLabel(
  settings: Settings,
  installedModel: ModelInfo | null,
): string {
  if (isReadyForCloud(settings)) return `Cloud · ${settings.selectedProvider}`;
  if (installedModel && isWebGpuAvailable()) return "On-device · Gemma";
  return "No engine configured";
}
